import _ from 'lodash';
import {useMemo} from 'react';

import {
  OrganizationSubscriptionType,
  useAccountSelectorContextProviderEntityQuery,
  useOrganizationSubscriptionsBasicInfoQuery,
  useViewerUserInfoQuery,
} from '../../../generated/graphql';
import {getAccountType} from '../../../pages/Billing/AccountSettings/util';
import {PlanName} from '../../../pages/Billing/util';
import {EntityType} from '../../../pages/EntityPage/useEntityPageData';
import {useViewer} from '../../../state/viewer/hooks';
import {
  getAccountPlanTypeFromPrimarySub,
  getPrimarySub,
} from '../../../util/accounts/pricing';
import {useRampFlagAccountSelector} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {type Account, AccountPlanType, AccountType} from './types';

type AccountList = {
  accountList: Account[];
  isLoading: boolean;
};
// Returned accountList includes a personal account only when the personal entity exists.
export function useAccountList(): AccountList {
  const viewer = useViewer();
  const {
    data: viewerOrgsResult,
    loading: organizationSubscriptionQueryLoading,
  } = useOrganizationSubscriptionsBasicInfoQuery({
    fetchPolicy: 'cache-first',
  });
  const enableAccountSelector = useRampFlagAccountSelector();
  const orgAccounts = useMemo(() => {
    return viewerOrgsResult?.viewer?.organizations.map(org => {
      const accountType = getAccountType(org);
      const subscription = getPrimarySub(org);
      const accountPlanType = getAccountPlanTypeFromPrimarySub(subscription);
      return {
        name:
          accountType === AccountType.Personal
            ? (viewer?.username ?? '')
            : org.name,
        orgType: org.orgType,
        accountType,
        accountPlanType,
        id: accountType === AccountType.Personal ? viewer?.id : org.id,
        personalOrgId:
          accountType === AccountType.Personal ? org.id : undefined,
        memberCount: org.memberCount,
        isEnterprise:
          subscription?.plan?.name === PlanName.Enterprise &&
          subscription?.subscriptionType !==
            OrganizationSubscriptionType.AcademicTrial &&
          subscription?.subscriptionType !==
            OrganizationSubscriptionType.Academic,
        username: viewer?.username ?? '',
      };
    });
  }, [viewerOrgsResult?.viewer?.organizations, viewer?.id, viewer?.username]);

  const viewerUserInfo = useViewerUserInfoQuery();
  const userInfo = viewerUserInfo?.data?.viewer?.userInfo;

  const {data: viewerEntityData, loading: viewerEntityLoading} =
    useAccountSelectorContextProviderEntityQuery({
      variables: {teamName: viewer?.username ?? ''},
      skip: !viewer?.username,
      fetchPolicy: 'cache-first',
    });

  const accountList = useMemo(() => {
    const sortedAccounts = _.sortBy(orgAccounts, 'name');
    const shouldHidePersonalEntity = userInfo?.isPersonalEntityHidden ?? false;
    const hasPersonalEntity =
      viewerEntityData?.entity != null &&
      viewerEntityData.entity?.entityType === EntityType.Personal;

    if (shouldHidePersonalEntity) {
      const orgsWithOutPersonalAccount = sortedAccounts.filter(
        a => a.accountType !== AccountType.Personal
      );
      if (orgsWithOutPersonalAccount.length > 0) {
        return orgsWithOutPersonalAccount;
      }
    }

    // users get personal type organization once they pay for storage plan
    const personalAccount = sortedAccounts.find(
      account => account?.accountType === AccountType.Personal
    );
    if (personalAccount == null) {
      if (hasPersonalEntity && !shouldHidePersonalEntity) {
        const dummyPersonalAccountData = {
          name: viewer?.username ?? '',
          accountType: AccountType.Personal,
          accountPlanType: AccountPlanType.Personal,
          id: enableAccountSelector ? viewer?.id : undefined,
          username: viewer?.username ?? '',
        };
        return [dummyPersonalAccountData, ...sortedAccounts];
      }
      return sortedAccounts;
    }

    // if user paid for personal account, move it to the front of the list
    const orgAccountList = sortedAccounts.filter(
      account => account?.accountType !== AccountType.Personal
    );
    return [personalAccount, ...orgAccountList];
  }, [
    orgAccounts,
    viewer?.username,
    viewer?.id,
    viewerEntityData,
    enableAccountSelector,
    userInfo,
  ]);

  return useMemo(
    () => ({
      accountList,
      isLoading:
        viewer?.username == null ||
        viewer?.id == null ||
        organizationSubscriptionQueryLoading ||
        viewerUserInfo?.loading ||
        viewerEntityLoading,
    }),
    [
      accountList,
      viewer?.username,
      viewer?.id,
      organizationSubscriptionQueryLoading,
      viewerUserInfo?.loading,
      viewerEntityLoading,
    ]
  );
}
